import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, CardContainer } from 'finara-components';
import React, { useState } from 'react';
import { toast } from 'react-hot-toast';

import { useCreateOrganizationWithAdmin } from '@/hooks/queries/useSuperAdminClients';
import type { CreateOrganizationWithAdminRequest } from '@/types/superAdminClients';

interface AddClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const AddClientModal: React.FC<AddClientModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<CreateOrganizationWithAdminRequest>({
    organizationName: '',
    description: '',
    adminName: '',
    adminEmail: '',
    adminPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);

  const createOrganizationMutation = useCreateOrganizationWithAdmin();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.organizationName.trim()) {
      toast.error('El nombre de la organización es requerido');
      return;
    }
    if (!formData.adminName.trim()) {
      toast.error('El nombre del administrador es requerido');
      return;
    }
    if (!formData.adminEmail.trim()) {
      toast.error('El email del administrador es requerido');
      return;
    }
    if (!formData.adminPassword || formData.adminPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    try {
      await createOrganizationMutation.mutateAsync(formData);
      toast.success('Cliente creado exitosamente');

      // Reset form
      setFormData({
        organizationName: '',
        description: '',
        adminName: '',
        adminEmail: '',
        adminPassword: '',
      });

      onSuccess();
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al crear el cliente';
      toast.error(errorMessage);
    }
  };

  const handleCancel = () => {
    setFormData({
      organizationName: '',
      description: '',
      adminName: '',
      adminEmail: '',
      adminPassword: '',
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black opacity-60 transition-opacity"
        onClick={handleCancel}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Escape') handleCancel();
        }}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <CardContainer
          className="bg-surface !opacity-100 relative max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6">
            <h2 className="text-xl font-bold text-brand-white mb-6">Crear Nuevo Cliente</h2>

            <form onSubmit={e => void handleSubmit(e)}>
              <div className="space-y-4">
                {/* Organization Information */}
                <div>
                  <h3 className="text-sm font-semibold text-brand-white mb-3">
                    Información de la Organización
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="organizationName" className="block text-sm font-medium text-brand-white mb-1">
                        Nombre de la Organización *
                      </label>
                      <input
                        type="text"
                        id="organizationName"
                        name="organizationName"
                        value={formData.organizationName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                        placeholder="Ej: Empresa Tech SA de CV"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-brand-white mb-1">
                        Descripción (Opcional)
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                        placeholder="Breve descripción de la organización"
                        rows={3}
                        maxLength={100}
                      />
                      <p className="text-xs text-muted mt-1">
                        Máximo 100 caracteres ({formData.description?.length || 0}/100)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Admin Information */}
                <div className="pt-4 border-t border-border">
                  <h3 className="text-sm font-semibold text-brand-white mb-3">
                    Información del Administrador
                  </h3>

                  <div className="space-y-4">
                    <div>
                      <label htmlFor="adminName" className="block text-sm font-medium text-brand-white mb-1">
                        Nombre Completo *
                      </label>
                      <input
                        type="text"
                        id="adminName"
                        name="adminName"
                        value={formData.adminName}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                        placeholder="Ej: Juan Pérez García"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="adminEmail" className="block text-sm font-medium text-brand-white mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="adminEmail"
                        name="adminEmail"
                        value={formData.adminEmail}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                        placeholder="admin@empresa.com"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="adminPassword" className="block text-sm font-medium text-brand-white mb-1">
                        Contraseña *
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="adminPassword"
                          name="adminPassword"
                          value={formData.adminPassword}
                          onChange={handleChange}
                          className="w-full px-3 py-2 pr-10 border border-border bg-surface text-brand-white placeholder:text-muted rounded-md focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue"
                          placeholder="Mínimo 8 caracteres"
                          required
                          minLength={8}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-brand-white focus:outline-none"
                          aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        >
                          {showPassword ? (
                            <VisibilityOff fontSize="small" />
                          ) : (
                            <Visibility fontSize="small" />
                          )}
                        </button>
                      </div>
                      <p className="text-xs text-muted mt-1">Mínimo 8 caracteres</p>
                    </div>
                  </div>
                </div>

                {/* Info Message */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-md p-3">
                  <p className="text-sm text-brand-blue">
                    <strong>Nota:</strong> El administrador podrá iniciar sesión inmediatamente con
                    las credenciales proporcionadas.
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 mt-6 pt-6 border-t border-border">
                <Button
                  type="submit"
                  variant="black"
                  disabled={createOrganizationMutation.isPending}
                  className="flex-1"
                >
                  {createOrganizationMutation.isPending ? 'Creando...' : 'Crear Cliente'}
                </Button>
                <Button
                  type="button"
                  variant="white"
                  onClick={handleCancel}
                  disabled={createOrganizationMutation.isPending}
                  className="flex-1"
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </div>
        </CardContainer>
      </div>
    </div>
  );
};
